import { useState } from 'react';
import { Box, Icon, Modal, Tag, Text, Title } from '@nimbus-ds/components';
import { noop } from 'lodash';
import { useNavegate } from 'App/hooks';
import { CancelAndSaveButtons } from 'commons/components';
import { AiIcon } from 'commons/components/GenerateWithAiLink/AiIcon';
import { useGetLanguage } from 'domains/Auth/hooks';
import { CATALOG_ROUTES } from 'domains/Catalog/catalogRoutes';
import { useTranslationCatalog } from 'domains/Catalog/hooks';
import { trackingProductListAddProductAiModalCreateClick } from 'domains/Catalog/Products/tracking';
import ErrorAIModal from './ErrorAIModal/ErrorAIModal';
import FakeProgressBarAIModal from './FakeProgressBarAIModal';
import { useImageGallery } from '../../../components';
import {
  MediaDropArea,
  MediaPreview,
} from '../../../components/ImageGallery/components';
import { useProductForm } from '../../../hooks';
import useGenerateProduct from '../../../hooks/useGenerateProduct/useGenerateProduct';
import { productDefault } from '../../../NewProductPage/constants';

const MAX_IMAGES_TO_LOAD = 3;
interface CreateFromImagesModalProps {
  open: boolean;
  onDismiss: () => void;
}

function CreateFromImagesModal({
  open,
  onDismiss,
}: Readonly<CreateFromImagesModalProps>): JSX.Element {
  const t = useTranslationCatalog();
  const language = useGetLanguage();
  const { goTo } = useNavegate();
  const [isGenerating, setIsGenerating] = useState(false);
  const [isError, setIsError] = useState(false);
  const [promise, setPromise] = useState<Promise<unknown> | null>(null);

  const { generateAIProduct } = useGenerateProduct();

  const {
    values: { images },
    handleChangeImages,
  } = useProductForm({
    initialValues: productDefault,
    onError: noop,
    onSuccess: noop,
    onSave: async () => {
      // No operation needed for onSave in this context
    },
    selectFirstOptionMetafields: '',
  });

  const handleDismiss = () => {
    handleChangeImages([]);
    setIsError(false);
    setIsGenerating(false);
    setPromise(null);
    onDismiss();
  };

  const submit = () => {
    setIsGenerating(true);
    setPromise(
      generateAIProduct({
        images: images.map((image) => ({
          url: image.publicUrl ?? '',
          id: image.id,
        })),
        lang: language,
      }),
    );
  };

  const handleSave = async () => {
    trackingProductListAddProductAiModalCreateClick({
      countImages: images.length.toString(),
    });
    submit();
  };

  const onError = () => {
    setIsGenerating(false);
    setIsError(true);
  };

  const onFinish = () => {
    setIsGenerating(false);
    handleDismiss();
    goTo(CATALOG_ROUTES.newProduct);
  };

  const { videoQuantity, handleOnchange, handleOnRemove, onRetryError } =
    useImageGallery({
      images,
      isEdit: false,
      onChange: handleChangeImages,
    });

  if (isGenerating && promise !== null) {
    return (
      <FakeProgressBarAIModal
        promise={promise}
        onFinish={onFinish}
        onError={onError}
      />
    );
  }

  if (isError) {
    return <ErrorAIModal onRetry={submit} onDismiss={handleDismiss} />;
  }

  return (
    <Modal
      open={open}
      onDismiss={isGenerating ? noop : handleDismiss}
      maxWidth="500px"
    >
      <Modal.Header>
        <Box display="flex" flexDirection="column" gap="1">
          <Box display="flex" flexDirection="row" gap="2" alignItems="center">
            <Title as="h4">{t('products.fromImages.modal.title')}</Title>
            <Tag appearance="ai-generative">
              <Icon source={<AiIcon size={12} />} />
              <Text fontSize="caption" lineHeight="caption">
                {t('products.fromImages.modal.tag')}
              </Text>
            </Tag>
          </Box>
          <Text>{t('products.fromImages.modal.description')}</Text>
        </Box>
      </Modal.Header>
      <Modal.Body padding="none">
        <Box display="flex" flexDirection="column" gap="2-5">
          <MediaDropArea
            videoQuantity={videoQuantity}
            onUploadImages={handleOnchange}
            customText={t('products.fromImages.modal.fileUpload')}
            allowUploadVideos={false}
            countItems={images.length}
            maxItemsToLoad={MAX_IMAGES_TO_LOAD}
          />
          <MediaPreview
            images={images}
            columns={MAX_IMAGES_TO_LOAD}
            showFirstImageAsMain={false}
            onRemove={handleOnRemove}
            onError={onRetryError}
          />
        </Box>
      </Modal.Body>
      <Modal.Footer>
        <CancelAndSaveButtons
          saveText={t('products.fromImages.modal.submit')}
          isDisabled={
            images.length === 0 ||
            images.some((image) => image.isLoading === true)
          }
          isLoading={isGenerating}
          cancelIsDisabled={isGenerating}
          onCancel={handleDismiss}
          onSave={handleSave}
        />
      </Modal.Footer>
    </Modal>
  );
}

export default CreateFromImagesModal;
